/**
 * RSS investigation collector.
 *
 * Fetches and parses RSS/Atom feeds for investigation collection tasks.
 * Filters articles by keywords and temporal bounds from the task query.
 * Routes through the RSS proxy (sidecar or Vercel edge function).
 */

import { getApiBase } from '@/app/api-router';
import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RSSArticle {
  title: string;
  description: string;
  link: string;
  pubDate: string;
  source: string;
}

export interface RSSCollectionResult {
  articles: RSSArticle[];
  rawText: string;
  articleCount: number;
  feedUrl: string;
}

/** Default timeout for RSS fetches (ms). */
const DEFAULT_TIMEOUT_MS = 15_000;

// ---------------------------------------------------------------------------
// XML parsing helpers
// ---------------------------------------------------------------------------

function getTextContent(parent: Element, tag: string): string | null {
  const el = parent.querySelector(tag);
  return el?.textContent?.trim() ?? null;
}

function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Feed parser
// ---------------------------------------------------------------------------

/**
 * Parse RSS 2.0 or Atom XML into article objects.
 */
function parseFeedXml(xml: string, sourceName: string): RSSArticle[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    console.warn(`[Sentinel] RSS XML parse error for ${sourceName}`);
    return [];
  }

  const articles: RSSArticle[] = [];

  // RSS 2.0 items
  const rssItems = doc.querySelectorAll('item');
  if (rssItems.length > 0) {
    rssItems.forEach(item => {
      const title = getTextContent(item, 'title');
      if (!title) return;

      const description = getTextContent(item, 'description') ?? '';
      const link = getTextContent(item, 'link') ?? '';
      const pubDate = getTextContent(item, 'pubDate') ?? '';

      articles.push({
        title,
        description: stripHtmlTags(description),
        link,
        pubDate,
        source: sourceName,
      });
    });
    return articles;
  }

  // Atom entries
  const atomEntries = doc.querySelectorAll('entry');
  atomEntries.forEach(entry => {
    const title = getTextContent(entry, 'title');
    if (!title) return;

    const summary = getTextContent(entry, 'summary')
      ?? getTextContent(entry, 'content')
      ?? '';
    const linkEl = entry.querySelector('link[href]');
    const link = linkEl?.getAttribute('href') ?? '';
    const updated = getTextContent(entry, 'updated')
      ?? getTextContent(entry, 'published')
      ?? '';

    articles.push({
      title,
      description: stripHtmlTags(summary),
      link,
      pubDate: updated,
      source: sourceName,
    });
  });

  return articles;
}

// ---------------------------------------------------------------------------
// Keyword matching
// ---------------------------------------------------------------------------

/**
 * Check if an article matches any of the provided keywords.
 * Case-insensitive search in title and description.
 */
function matchesKeywords(article: RSSArticle, keywords: string[]): boolean {
  if (keywords.length === 0) return true;

  const searchText = `${article.title} ${article.description}`.toLowerCase();
  return keywords.some(kw => searchText.includes(kw.toLowerCase()));
}

/**
 * Check if an article falls within temporal bounds.
 */
function withinTemporalBounds(
  article: RSSArticle,
  start?: string,
  end?: string,
): boolean {
  if (!article.pubDate) return true; // No date = include (conservative)
  if (!start && !end) return true;

  const articleDate = new Date(article.pubDate).getTime();
  if (isNaN(articleDate)) return true; // Unparseable = include

  if (start) {
    const startMs = new Date(start).getTime();
    if (!isNaN(startMs) && articleDate < startMs) return false;
  }

  if (end) {
    const endMs = new Date(end).getTime();
    if (!isNaN(endMs) && articleDate > endMs) return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Collector
// ---------------------------------------------------------------------------

/**
 * Fetch and parse an RSS feed for a collection task.
 * Filters articles by keywords and temporal bounds from the task query.
 * Returns concatenated article text suitable for entity extraction.
 */
export async function collectFromRSS(
  task: CollectionTask,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<RSSCollectionResult> {
  const query = task.query as Record<string, unknown>;
  const feedUrl = (query.url as string) ?? '';

  if (!feedUrl) {
    throw new Error('RSS task requires a feed URL in query.url');
  }

  const keywords = (query.keywords as string[]) ?? [];
  const temporal = query.temporalBounds as { start?: string; end?: string } | undefined;

  // Fetch via RSS proxy
  const proxyUrl = `${getApiBase()}/api/rss/feed?url=${encodeURIComponent(feedUrl)}`;

  const response = await globalThis.fetch(proxyUrl, {
    headers: {
      Accept: 'application/rss+xml, application/xml, text/xml, application/atom+xml',
    },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) {
    throw new Error(`RSS fetch failed for ${feedUrl}: ${response.status}`);
  }

  const xml = await response.text();
  const sourceName = task.sourceId;

  // Parse feed
  let articles = parseFeedXml(xml, sourceName);

  // Filter by keywords
  if (keywords.length > 0) {
    articles = articles.filter(a => matchesKeywords(a, keywords));
  }

  // Filter by temporal bounds
  if (temporal?.start || temporal?.end) {
    articles = articles.filter(a =>
      withinTemporalBounds(a, temporal.start, temporal.end),
    );
  }

  // Build raw text for entity extraction
  const textParts: string[] = [];

  for (const article of articles) {
    const parts: string[] = [];

    if (article.title) {
      parts.push(article.title);
    }
    if (article.description) {
      parts.push(article.description);
    }
    if (article.link) {
      parts.push(`URL: ${article.link}`);
    }
    if (article.pubDate) {
      parts.push(`Date: ${article.pubDate}`);
    }
    parts.push(`Source: ${article.source}`);

    textParts.push(parts.join('\n'));
  }

  const rawText = textParts.join('\n\n---\n\n');

  return {
    articles,
    rawText: rawText || 'No relevant articles found in feed.',
    articleCount: articles.length,
    feedUrl,
  };
}
